import { useMutation } from '@apollo/client';
import {
  Box,
  Button,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { CaretDownFill } from '@styled-icons/bootstrap/CaretDownFill';
import useConfig from 'hooks/useConfig';
import { useLogEvent } from 'hooks/useLogEvent';
import useMessages from 'hooks/useMessages';
import ThemeButton from 'DesignSystem/Buttons/ThemeButton/ThemeButton';
import { UPDATE_QR_DESIGN } from 'graphql/secure/mutations';
import React, { FC, Fragment, PropsWithChildren, useRef, useState } from 'react';
import { ESupportedImageCustomFormats } from 'FeatureModules/ProductPage/types/product.types';
import { IProductQrCodeDesign } from 'FeatureModules/ProductPage/types/product.types';
import { downloadFile } from 'utils/download.utils';
import FlexBox from 'Components/FlexBox';

interface IQRDownloadButtonProps extends PropsWithChildren {
  qrCode: IProductQrCodeDesign;
  productId: string;
  title: string;
}

const ImageTypeButton = styled(Button)(({ theme }) => ({
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  height: 22,
  border: 'none',
}));

const options = Object.values(ESupportedImageCustomFormats);

const QRDownloadButton: FC<IQRDownloadButtonProps> = ({ qrCode, productId, title }) => {
  const { logEvent } = useLogEvent();
  const { setErrorMessage } = useMessages();
  const config = useConfig();

  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const suffix = options[selectedIndex] === ESupportedImageCustomFormats.EPS ? '.eps' : '';
  const [updateQRDesign, { loading }] = useMutation(UPDATE_QR_DESIGN, {
    onCompleted: async ({ updateQRDesign: blob }) => {
      const url = URL.createObjectURL(blob);
      // The EPS image extension is not added to the file name automatically. For other formats it will be added by the browser.
      await downloadFile(url, `${title}_qr_code${suffix}`);
      URL.revokeObjectURL(url);
      logEvent('DOWNLOAD_QR_CODE');
    },
    onError: () => {
      setErrorMessage('We could not retrieve your QR code. Please contact our support.');
    },
  });

  const handleMenuItemClick = (index: number) => {
    setSelectedIndex(index);
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen);
  };

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as Node)) {
      return;
    }
    setOpen(false);
  };

  const handleUpdate = async () => {
    const selectedFormat = options[selectedIndex];
    return updateQRDesign({
      variables: {
        qrCodeDesign: {
          design: {
            ...qrCode.design,
            qr_code_text: config.generateRedirectURL(productId),
            // api accepts only JPG
            image_format: selectedFormat === 'JPEG' ? 'JPG' : selectedFormat,
            image_width: 512,
          },
          responseType: 'blob',
        },
      },
    });
  };

  return (
    <Fragment>
      <FlexBox display="flex" flexDirection="column" gap={1} alignItems="flex-start">
        <ImageTypeButton
          ref={anchorRef}
          aria-controls={open ? 'split-button-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-label="select merge strategy"
          aria-haspopup="menu"
          onClick={handleToggle}
          variant="outlined"
          color="inherit"
          size="small"
          endIcon={<CaretDownFill size={12} />}
        >
          <Box mr={0.5}>{`${options[selectedIndex]}`}</Box>
        </ImageTypeButton>

        <ThemeButton loading={loading} size="small" color="BLUE_ICE" onClick={handleUpdate}>
          Download QR code
        </ThemeButton>
      </FlexBox>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        style={{
          zIndex: 1400,
        }}
        transition
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id="split-button-menu">
                  {options.map((option, index) => (
                    <MenuItem
                      key={option}
                      selected={index === selectedIndex}
                      onClick={() => handleMenuItemClick(index)}
                    >
                      {option}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Fragment>
  );
};

export default QRDownloadButton;
